<template>
  <v-card v-if="purchase">
    <v-card-title primary-title>
      <v-btn
        color="secondary"
        icon
        flat
        class="ma-0"
        @click.stop="goToPurchaseList"
        ><v-icon>$vuetify.icons.screener_back</v-icon></v-btn
      >
      <div class="headline secondary--text">
        {{ $t('screener.monetization.sale_detail.title') }}
      </div>
      <v-spacer></v-spacer>
      <!-- Refund option available only when
              -Auth user is company ownder.admin
              -Amount left on the screener
        -->
      <v-btn
        v-if="
          purchase.total_amount - purchase.refunded_amount &&
            userRoleInCompany >= DEFINES.ROLE_ADMIN
        "
        class="mx-0"
        color="green"
        small
        dark
        @click.stop="onBeforeRefund(purchase, null, true)"
        >{{ $t('screener.btn.full_refund') }}</v-btn
      >

      <v-btn
        v-if="
          !purchase.is_refunded && user_role == DEFINES.USER_ROLE_SUPER_ADMIN
        "
        class="mx-1"
        color="green"
        small
        dark
        @click.stop="
          onBeforePayout(
            DEFINES.MONETIZATION.PAYOUT.DISPUTE_FROM_CLIENT,
            purchase
          )
        "
        >{{ $t('screener.btn.dispute') }}</v-btn
      >

      <v-btn
        v-if="
            user_role == DEFINES.USER_ROLE_SUPER_ADMIN &&
            purchase.pricing == null
        "
        class="mx-1"
        color="green"
        small
        dark
        @click.stop="
          onBeforePayout(DEFINES.MONETIZATION.PAYOUT.ADJUSTMENT, purchase)
        "
        >{{ $t('screener.btn.add.remove.adjustment') }}</v-btn
      >
    </v-card-title>
    <v-card-text v-if="purchase">
      <div class="mb-4">
        <h3>{{ $t('screener.monetization.about_sale.title') }}</h3>
        <v-divider class="my-2"></v-divider>

        <v-layout row wrap>
          <v-flex xs6 sm6 md4>
            <!-- Participant -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.participant')
              }}</v-flex>
              <v-flex xs6>
                <router-link
                  :to="{
                    name: 'consumer.index',
                    params: {
                      consumer_id: purchase.consumer_id,
                    },
                  }"
                  >{{ purchase.consumer_email }}</router-link
                >
              </v-flex>
            </v-layout>

            <!-- Pricing Plan -->
            <v-layout row wrap class="py-1" v-if="purchase.pricing">
              <v-flex xs6 class="grey--text text--darken-1">
                {{ $t('screener.monetization.pricing_plan') }}
              </v-flex>
              <v-flex xs6>
                <router-link
                  v-if="
                    purchase.pricing &&
                      purchase.purchase_type ==
                        DEFINES.MONETIZATION.PRICING_TYPE.GROUP_PURCHASE
                  "
                  :to="{
                    name: 'group.ticket.save',
                    params: {
                      pricing_id: purchase.monetization_pricing_id,
                    },
                  }"
                >
                  {{ purchase.pricing.name }}</router-link
                >
                <span v-else>
                    {{ $t('screener.not_available.title') }}
                </span>
              </v-flex>
            </v-layout>

            <!-- Pricing plan type -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.plan_type')
              }}</v-flex>
              <v-flex xs6>
                {{ DEFINES.MONETIZATION.PURCHASE_TYPE[purchase.purchase_type] }}
              </v-flex>
            </v-layout>

            <!-- in-venue event -->
            <v-layout row wrap class="py-1" v-if="!purchase.pricing">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.in_venue_event')
              }}</v-flex>
              <v-flex xs6>
                {{ purchase.in_venue.event_title}}
              </v-flex>
            </v-layout>

            <!-- Booking Id -->
            <v-layout row wrap class="py-1" v-if="!purchase.pricing">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.booking_id')
              }}</v-flex>
              <v-flex xs6>
                {{ purchase.booking_id }}
              </v-flex>
            </v-layout>

            <!-- Plan cost -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">
                {{ $t('screener.table.header.plan_cost') }}
              </v-flex>
              <v-flex xs6>
                <div v-if="purchase.pricing">
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    purchase.pricing.price
                  }}
                </div>
                <div v-else>{{ $t('screener.not_available.title') }}</div>
              </v-flex>
            </v-layout>

            <!-- Affiliation Share Percentage -->
            <v-layout
              v-if="purchase.affiliation_revenue_percentage"
              row
              wrap
              class="py-1"
            >
              <v-flex xs6 class="grey--text text--darken-1">
                {{ $t('screener.affiliation.share_percentage.title') }}
              </v-flex>
              <v-flex xs6>
                <span v-if="purchase.total_amount > 0"
                  >{{ purchase.affiliation_revenue_percentage }}&nbsp;%</span
                >
                <span v-else>{{ $t('screener.not_available.title') }}</span>
              </v-flex>
            </v-layout>

            <!-- Commission Percentage -->
            <v-layout
              v-if="purchase.system_commission_percentage"
              row
              wrap
              class="py-1"
            >
              <v-flex xs6 class="grey--text text--darken-1">
                System Commission Percentage
              </v-flex>
              <v-flex xs6>
                <span v-if="purchase.total_amount > 0"
                  >{{ purchase.system_commission_percentage }}&nbsp;%</span
                >
                <span v-else>{{ $t('screener.not_available.title') }}</span>
              </v-flex>
            </v-layout>

            <!-- Number of screenings -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.monetization.number_of_screening')
              }}</v-flex>
              <v-flex xs6>
                <span v-if="purchase.pricing">{{
                  purchase.pricing.screening_ids.length
                }}</span>
                <span v-else>1</span>
              </v-flex>
            </v-layout>

            <!-- Created At -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.created_at')
              }}</v-flex>
              <v-flex xs6>
                <span>{{ fmtTime(purchase.created_at) }}</span>
              </v-flex>
            </v-layout>

            <!-- Receipt -->
            <v-layout v-if="purchase.receipt_url" row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.monetization.receipt')
              }}</v-flex>
              <v-flex xs6>
                <a :href="purchase.receipt_url" target="blank">{{
                  $t('screener.view.title')
                }}</a>
              </v-flex>
            </v-layout>

            <!-- Status -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.status')
              }}</v-flex>
              <v-flex xs6>
                <v-chip v-if="purchase.is_paid" color="success" small dark>{{
                  $t('screener.status.paid')
                }}</v-chip>
                <v-chip v-if="purchase.is_refunded" color="orange" small dark>{{
                  $t('screener.status.refunded')
                }}</v-chip>
              </v-flex>
            </v-layout>

            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.payment_gateway.title')
              }}</v-flex>
              <v-flex xs6>
                <template v-if="purchase.payment_gateway">
                  <!-- Stripe/PayPal/Admin Pay -->
                  <v-chip class="my-0 px-0" outline color="primary" small dark>
                    <b>{{
                      DEFINES.PAYMENT_GATEWAYS[
                        purchase.payment_gateway.toUpperCase()
                      ].name
                    }}</b>
                  </v-chip>
                </template>
                <span v-else>{{ $t('screener.not_available.title') }}</span>
              </v-flex>
            </v-layout>
            <v-layout
              row
              wrap
              class="py-1"
              v-if="
                purchase.payment_gateway &&
                  purchase.payment_gateway == 'meshulam'
              "
            >
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.payment_gateway.meshulam.account_owner')
              }}</v-flex>
              <v-flex xs6>
                <template>
                  <!-- Stripe/PayPal/Admin Pay -->
                  <v-chip
                    class="my-0 px-0"
                    outline
                    color="primary"
                    small
                    dark
                    v-if="
                      purchase.is_pg_account_owner_mew != null &&
                        typeof purchase.is_pg_account_owner_mew == 'boolean'
                    "
                  >
                    <b>{{
                      purchase.is_pg_account_owner_mew === true
                        ? $t(
                            'screener.payment_gateway.meshulam.super_admin_account'
                          )
                        : $t(
                            'screener.payment_gateway.meshulam.company_account'
                          )
                    }}</b>
                  </v-chip>
                </template>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs6 sm6 md4 offset-md2 offset-xs0>
            <!-- Currency -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.currency')
              }}</v-flex>
              <v-flex xs6>{{ purchase.currency }}</v-flex>
            </v-layout>

            <!-- Coupon -->
            <v-layout v-if="purchase.coupon_code" row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.coupon')
              }}</v-flex>
              <v-flex xs6
                ><router-link
                  :to="{
                    name: 'company.coupon.update',
                    params: {
                      coupon_id: purchase.coupon_id,
                    },
                  }"
                >
                  {{ purchase.coupon_code }}</router-link
                ></v-flex
              >
            </v-layout>

            <!-- Discount -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.discount')
              }}</v-flex>
              <v-flex xs6>
                <span v-if="purchase.coupon_code > 0" class="red--text"
                  ><strong>-&nbsp; </strong></span
                >
                <span v-if="purchase.applied_coupon_discount"
                  >{{ getCurrencySymbol(purchase.currency) }}&nbsp;
                  {{ purchase.applied_coupon_discount }}</span
                >
                <span v-else-if="purchase.discount_percentage">
                  {{ purchase.discount_percentage }} %</span
                >
                <span v-else>NA</span>
              </v-flex>
            </v-layout>

            <!-- Total amount -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.total_amount')
              }}</v-flex>
              <v-flex xs6>
                <span v-if="purchase.total_amount > 0" class="green--text"
                  ><strong>+&nbsp;</strong></span
                >
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    purchase.total_amount
                  }}</span
                >
              </v-flex>
            </v-layout>

            <!-- Gateway Fees -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.gateway_fees')
              }}</v-flex>
              <v-flex xs6>
                <span v-if="purchase.gateway_fee > 0" class="red--text"
                  ><strong>-&nbsp;</strong></span
                >
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    purchase.gateway_fee ? purchase.gateway_fee : 0
                  }}</span
                >
              </v-flex>
            </v-layout>

            <!-- Total refunded Amount -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.table.header.refunded_amount')
              }}</v-flex>
              <v-flex xs6>
                <span v-if="purchase.refunded_amount > 0" class="red--text"
                  ><strong>-&nbsp;</strong></span
                >
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    getTotalRefundedAmount
                  }}</span
                >
              </v-flex>
            </v-layout>

            <!-- Affiliation share -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.affiliation.share.title')
              }}</v-flex>
              <v-flex xs6>
                <span v-if="purchase.affiliation_share > 0" class="red--text"
                  ><strong>-&nbsp;</strong></span
                >
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    purchase.affiliation_share
                      ? purchase.affiliation_share
                      : '0.00'
                  }}</span
                >
                <v-tooltip top color="white">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" small class="px-2"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <div
                    class="grey--text text--darken-3 body-1"
                    v-html="$t('screener.affiliation.share.tooltip')"
                  ></div>
                </v-tooltip>
              </v-flex>
            </v-layout>

            <!-- System Share -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.system_share.title')
              }}</v-flex>
              <v-flex xs6>
                <span v-if="purchase.system_share > 0" class="red--text"
                  ><strong>-&nbsp;</strong></span
                >
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    purchase.system_share ? purchase.system_share : '0.00'
                  }}</span
                >
                <v-tooltip top color="white">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" small class="px-2"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <div
                    class="grey--text text--darken-3 body-1"
                    v-html="$t('screener.system_share.tooltip')"
                  ></div>
                </v-tooltip>
              </v-flex>
            </v-layout>

            <!-- Earned Amount -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1"
                >{{ $t('screener.table.header.earned_amount') }}
              </v-flex>
              <v-flex xs6>
                <span>
                  <b>
                    {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                      getTotalEarnedAmount
                    }}</b
                  ></span
                >
                <v-tooltip top color="white">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" small class="px-2"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <div
                    class="grey--text text--darken-3 body-1"
                    v-html="$t('screener.monetization.earned_amount.tooltip')"
                  ></div>
                </v-tooltip>
              </v-flex>
            </v-layout>

            <!-- Remaining Total Refundable Amount -->
            <v-layout row wrap class="py-1">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.monetization.remaining_refundable_amount')
              }}</v-flex>
              <v-flex xs6>
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    getTotalRemainingRefundableAmount
                  }}</span
                >
              </v-flex>
            </v-layout>

            <v-layout row wrap class="py-1" v-if="purchase.dispute_amount">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.monetization.dispute_amount')
              }}</v-flex>
              <v-flex xs6>
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    purchase.dispute_amount ? purchase.dispute_amount : 0
                  }}</span
                >
              </v-flex>
            </v-layout>
            <v-layout row wrap class="py-1" v-if="purchase.adjustment_amount">
              <v-flex xs6 class="grey--text text--darken-1">{{
                $t('screener.monetization.adjustment_amount')
              }}</v-flex>
              <v-flex xs6>
                <span>
                  {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                    purchase.adjustment_amount ? purchase.adjustment_amount : 0
                  }}</span
                >
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </div>
      <!-- About screenings -->
      <div v-if="purchase.pricing">
        <h3>{{ $t('screener.table.header.screenings') }}</h3>
        <v-divider class="mt-2"></v-divider>
        <v-layout row wrap>
          <v-flex xs12>
            <v-data-table
              v-if="purchase.pricing.screening_ids.length"
              :headers="table_headers"
              :items="screenings"
              hide-actions
              :loader="loader"
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ props.index + 1 }}</td>
                  <td class="body-2">
                    <router-link
                      :to="{
                        name: 'screener.save',
                        params: {
                          screener_id: props.item.id,
                        },
                      }"
                      >{{ props.item.title }}</router-link
                    >
                  </td>
                  <!-- Total to refund -->
                  <td>
                    {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                      getPerScreeningAmount
                    }}
                  </td>
                  <!-- Refunded Amount -->
                  <td>
                    <span
                      v-if="getScreeningRefundedAmount(props.item.id)"
                      class="red--text"
                      ><strong>-&nbsp;</strong></span
                    >
                    {{ getCurrencySymbol(purchase.currency) }}&nbsp;{{
                      getScreeningRefundedAmount(props.item.id)
                    }}
                  </td>
                  <!-- Remaining Refundable Amount -->
                  <td>
                    {{ getCurrencySymbol(purchase.currency) }}&nbsp;
                    {{ getRemainingRefunadleAmountForScreening(props.item.id) }}
                  </td>
                  <td>
                    <v-btn
                      v-if="
                        getRemainingRefunadleAmountForScreening(
                          props.item.id
                        ) && userRoleInCompany >= DEFINES.ROLE_ADMIN
                      "
                      color="green"
                      small
                      dark
                      @click.stop="onBeforeRefund(purchase, props.item)"
                      >{{ $t('screener.btn.refund') }}</v-btn
                    >
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </div>
    </v-card-text>
    <Refund
      :plan="plan"
      :refundable_amount.sync="amount"
      :currency="currency"
      :is_full_refund="is_full_refund"
      :dialog="show_refund_confirmation"
      :reason.sync="reason"
      @onCancel="resetRefund"
      @onRefund="refund"
      :isAmountEditable="purchase.pricing != null"
    >
      <div
        slot="note"
        class="greyDarken2--text"
        v-html="$t('screener.refund.warning')"
      ></div>
    </Refund>
    <Payout
      :currency="currency"
      :payout_amount.sync="payout_amount"
      :client_note.sync="client_note"
      :admin_note.sync="admin_note"
      :dialog="show_payout_confirmation"
      :type="payout_type"
      @onCancel="resetPayout"
      @onPayout="addPayout"
      @onRemoval="removePayout"
    >
    </Payout>
  </v-card>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import DEFINES from '@/defines';
import {utcToTz, tzToUtc, getCurrencySymbol} from '@/helper';
import Refund from '@/components/screener/monetization/Refund';
import Payout from '@/components/screener/monetization/Payout';
import i18n from '@/i18n';

const data = function() {
  return {
    purchase: null,
    table_headers: [
      {
        text: 'S.No.',
        align: 'left',
        sortable: false,
        value: 'sn',
      },
      {
        text: i18n.t('rms.field.title.label'),
        sortable: true,
        align: 'left',
        value: 'title',
        tooltip: '',
      },
      {
        text: i18n.t('screener.monetization.total_refundable_amount'),
        sortable: false,
        align: 'left',
        value: 'refund',
        tooltip: '',
      },
      {
        text: i18n.t('screener.table.header.refunded_amount'),
        sortable: false,
        align: 'left',
        value: 'refundable_amount',
        tooltip: '',
      },
      {
        text: i18n.t('screener.monetization.remaining_refundable_amount'),
        sortable: false,
        align: 'left',
        value: 'refund',
        tooltip: '',
      },
      {
        text: i18n.t('rms.table.header.action'),
        sortable: false,
        align: 'left',
        value: 'action',
        tooltip: '',
      },
    ],
    show_refund_confirmation: false,
    consumer_id: null,
    amount: null,
    is_full_refund: false,
    plan: '',
    refundable_screening_ids: [],
    currency: '',
    purchase_id: null,
    reason: null,
    screenings: [],
    loader: false,
    client_note: null,
    admin_note: null,
    company_id: null,
    payout_amount: null,
    show_payout_confirmation: false,
    payout_type: null,
  };
};
const components = {
  Refund,
  Payout,
};

// Computed
const computed = {
  ...mapState({
    user_role: (state) => state.user.profile.role,
  }),
  ...mapGetters('company', {
    timezone: 'getSelectedCompanyTimezone',
    selected_company_id: 'getSelected',
  }),
  userRoleInCompany() {
    return this.$store.getters['company/userRoleInCompany'](
      this.selected_company_id
    );
  },
  getPerScreeningAmount() {
    if (this.purchase) {
      var per_screening_total_to_refund = this.purchase.pricing
        ? this.purchase.total_amount /
          this.purchase.pricing.screening_ids.length
        : this.purchase.total_amount;
      return per_screening_total_to_refund > 0
        ? parseFloat(per_screening_total_to_refund).toFixed(2)
        : 0;
    }
  },
  getTotalEarnedAmount() {
    if (this.purchase) {
      var earned_amount =
        this.purchase.total_amount -
        this.purchase.gateway_fee -
        this.getTotalRefundedAmount -
        this.purchase.affiliation_share -
        this.purchase.system_share;
      return earned_amount ? parseFloat(earned_amount.toFixed(2)) : 0;
    }
  },
  getTotalRemainingRefundableAmount() {
    if (this.purchase) {
      var remaining_refunable_amount =
        this.purchase.total_amount - this.getTotalRefundedAmount;
      return remaining_refunable_amount && remaining_refunable_amount > 0
        ? remaining_refunable_amount.toFixed(2)
        : 0;
    }
  },
  getTotalRefundedAmount() {
    if (this.purchase) {
      return this.purchase.refunded_amount > 0
        ? this.purchase.refunded_amount
        : 0;
    }
  },
};

const methods = {
  getCurrencySymbol(currency) {
    return getCurrencySymbol(currency);
  },
  // convert utc to comapny timezone
  fmtTime(input) {
    return utcToTz(input, this.timezone, 'Do MMM YYYY, h:mm a');
  },
  async getPurchase() {
    if (this.$route.params.purchase_id == undefined) {
      return;
    }
    try {
      this.purchase = await this.$store.dispatch('monetization/getPurchase', {
        purchase_id: this.$route.params.purchase_id,
      });
    } catch (error) {
      this.$store.dispatch('app/showMessage', {
        message: error.message,
        color: 'error',
      });
    }
  },
  onBeforeRefund(purchase, screener = null, is_full_refund = false) {
    this.is_full_refund = is_full_refund;
    var vue = this;
    this.amount = this.getTotalRemainingRefundableAmount;
    if (is_full_refund) {
      if (purchase.pricing) {
        this.refundable_screening_ids = purchase.pricing.screening_ids
          .map(function(screening_id) {
            if (vue.getRemainingRefunadleAmountForScreening(screening_id)) {
              return screening_id;
            }
          })
          .filter(function(screening_id) {
            return screening_id !== undefined;
          });
      } else {
        this.refundable_screening_ids.push(this.purchase.in_venue.screener_id);
      }
      this.amount = this.getTotalRemainingRefundableAmount;
      this.plan = purchase.pricing
        ? purchase.pricing.name
        : purchase.in_venue.event_title;
    } else {
      this.amount = this.getRemainingRefunadleAmountForScreening(screener.id);
      this.refundable_screening_ids.push(screener.id);
      this.plan = screener.title;
    }
    this.currency = purchase.currency;
    this.consumer_id = purchase.consumer_id;
    this.purchase_id = purchase.id;
    this.show_refund_confirmation = true;
  },
  getRemainingRefunadleAmountForScreening(screener_id) {
    var rem_ref_amt =
      this.getPerScreeningAmount - this.getScreeningRefundedAmount(screener_id);
    return rem_ref_amt > 0 ? rem_ref_amt.toFixed(2) : 0;
  },
  resetRefund() {
    this.show_refund_confirmation = false;
    this.is_full_refund = false;
    this.refundable_screening_ids = [];
    this.amount = null;
    this.consumer_id = null;
    this.currency = null;
    this.purchase_id = null;
    this.reason = null;
  },
  async refund() {
    this.show_refund_confirmation = false;
    var payload = this.prepareRefundPayload();
    // return;
    try {
      if (this.purchase.pricing) {
        var response = await this.$store.dispatch('consumer/refund', payload);
      } else {
        payload.booking_id = this.purchase.booking_id;
        payload.screener_id = this.purchase.in_venue.screener_id;
        var response = await this.$store.dispatch(
          'booking/cancelBooking',
          payload
        );
      }

      var paymentGateway = response.purchase
        ? response.purchase.payment_gateway
        : response.booking.purchase.payment_gateway;
      if (!this.DEFINES.REFUND_SUPPORTING_GATEWAYS.includes(paymentGateway)) {
        await this.$store.dispatch('app/showMessage', {
          message:
            this.purchase.pricing != null
              ? i18n.t(
                  'screener.purchase.manual.cancellation.required.message',
                  {
                    payment_gateway: response.purchase.payment_gateway,
                  }
                )
              : i18n.t(
                  'screener.booking.seats.manual.cancelation.required.message',
                  {
                    payment_gateway: response.booking.purchase.payment_gateway,
                  }
                ),
          color: 'success',
        });
      } else {
        await this.$store.dispatch('app/showMessage', {
          message: i18n.t('screener.refund.success'),
          color: 'success',
        });
      }

      this.resetRefund();
      this.getPurchase();
    } catch (error) {
      this.resetRefund();
      this.$store.dispatch('app/showMessage', {
        message: error.message,
        color: 'error',
      });
    }
  },
  prepareRefundPayload() {
    var total_amount = this.amount;
    var per_screening_amount = this.amount;
    if (this.is_full_refund) {
      per_screening_amount = this.amount / this.refundable_screening_ids.length;
    }
    var vue = this;
    var count = this.refundable_screening_ids.length;
    var refundable_items = this.refundable_screening_ids.map(function(
      screening_id
    ) {
      --count;
      var ref_amount = per_screening_amount;
      if (
        per_screening_amount >
        vue.getRemainingRefunadleAmountForScreening(screening_id)
      ) {
        ref_amount = vue.getRemainingRefunadleAmountForScreening(screening_id);
        total_amount -= ref_amount;
        per_screening_amount = total_amount / count;
      }
      return {
        screening_id: screening_id,
        refundable_amount: ref_amount,
        reason: vue.reason,
        currency: vue.currency,
      };
    });
    return {
      purchase_id: this.purchase_id,
      consumer_id: this.consumer_id,
      currency: this.currency,
      refundable_amount: this.amount,
      refundable_items: refundable_items,
      is_full_refund: this.is_full_refund,
      reason: this.reason,
    };
  },
  getScreeningRefundedAmount(screener_id) {
    if (this.purchase && this.purchase.refunded_screenings) {
      var refunded_screenings = Object.values(
        this.purchase.refunded_screenings
      );
      let screening = refunded_screenings.find((refunded_screening) => {
        return refunded_screening.screening_id == screener_id;
      });
      return screening ? screening.refundable_amount : 0;
    }
    return 0;
  },
  goToPurchaseList() {
    this.$router.push({
      name: 'purchase.index',
    });
  },
  async getPricingScreenings() {
    this.loader = true;
    try {
      this.screenings = await this.$store.dispatch(
        'monetization/getPricingScreenings',
        {
          pricing_id: this.purchase.pricing.id,
        }
      );
      this.loader = false;
    } catch (error) {
      this.loader = false;
      this.$store.dispatch('app/showMessage', {
        message: error.message,
        color: 'error',
      });
    }
  },

  async onBeforePayout(type, purchase) {
    this.show_payout_confirmation = true;
    this.purchase_id = purchase.id;
    this.currency = purchase.currency;
    this.company_id = purchase.company_id;
    this.payout_type = type;
    this.payout_amount =
      this.payout_type == DEFINES.MONETIZATION.PAYOUT.DISPUTE_FROM_CLIENT
        ? purchase.dispute_amount
        : purchase.adjustment_amount;
  },

  async addPayout() {
    try {
      let payload;
      payload = {
        purchase_id: this.purchase_id,
        amount: this.payout_amount,
        client_note: this.client_note,
        admin_note: this.admin_note,
        company_id: this.company_id,
        type: this.payout_type,
      };
      var response = await this.$store.dispatch(
        'monetization/addPayout',
        payload
      );
      if (this.payout_type == DEFINES.MONETIZATION.PAYOUT.DISPUTE_FROM_CLIENT) {
        this.purchase.dispute_amount = response.payout.amount;
        await this.$store.dispatch('app/showMessage', {
          message: i18n.t('screener.dispute.success'),
          color: 'success',
        });
      } else if (this.payout_type == DEFINES.MONETIZATION.PAYOUT.ADJUSTMENT) {
        this.purchase.adjustment_amount = response.payout.amount;
        await this.$store.dispatch('app/showMessage', {
          message: i18n.t('screener.adjustment.success'),
          color: 'success',
        });
      }
      this.getPurchase();
    } catch (error) {
      this.$store.dispatch('app/showMessage', {
        message: error.message,
        color: 'error',
      });
    } finally {
      this.resetPayout();
    }
  },
  resetPayout() {
    this.show_payout_confirmation = false;
    this.client_note = null;
    this.admin_note = null;
    this.payout_amount = null;
    this.payout_type = null;
  },
  async removePayout() {
    try {
      let payload;
      payload = {
        purchase_id: this.purchase_id,
        company_id: this.company_id,
        type: this.payout_type,
      };
      var response = await this.$store.dispatch(
        'monetization/removePayout',
        payload
      );
      if (this.payout_type == DEFINES.MONETIZATION.PAYOUT.DISPUTE_FROM_CLIENT) {
        await this.$store.dispatch('app/showMessage', {
          message: i18n.t('screener.dispute.settled.success'),
          color: 'success',
        });
      } else if (this.payout_type == DEFINES.MONETIZATION.PAYOUT.ADJUSTMENT) {
        await this.$store.dispatch('app/showMessage', {
          message: i18n.t('screener.adjustment.settled.success'),
          color: 'success',
        });
      }
      this.getPurchase();
    } catch (error) {
      this.$store.dispatch('app/showMessage', {
        message: error.message,
        color: 'error',
      });
    }finally {
      this.resetPayout();
    }
  },
};
export default {
  name: 'PurchaseDetail',
  data: data,
  computed: computed,
  components: components,
  watch: {
    purchase(value) {
      if (value && value.pricing) {
        this.getPricingScreenings();
      }
    },
  },
  mounted() {
    this.getPurchase();
    if (this.purchase && this.purchase.pricing) {
      this.getPricingScreenings();
    }
  },
  methods: methods,
};
</script>

<style></style>
